import React, { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Rendered in a sticky footer. Omit for a dialog with nothing to confirm. */
  footer?: React.ReactNode;
  size?: ModalSize;
  /** One line under the title. Use it instead of a first paragraph of body copy. */
  description?: string;
  /**
   * Rendered inline after the title — a <HelpTrigger>, a status chip. Keep it to
   * one small thing; anything with its own label belongs in the footer.
   */
  titleAccessory?: React.ReactNode;
  /**
   * When true, Escape and backdrop clicks do not close. For a form with unsaved
   * input — losing typing to a stray click is the worst thing a dialog can do.
   * The close button still works, so the user always has a deliberate way out.
   */
  dismissLocked?: boolean;
  /**
   * A band between the header and the scrolling body that does NOT scroll —
   * for the search field above a long pick-list. Leaving search in the body
   * means it scrolls out of reach exactly when the list is long enough to need
   * it.
   */
  subheader?: React.ReactNode;
  /** `raised` is the default. `flat` drops the shadow for a reading-only dialog. */
  elevation?: 'raised' | 'flat';
  className?: string;
  /**
   * Overrides the body's padding/scroll classes. For a dialog whose content is
   * its own scrolling surface — an iframe, an embedded page — where the
   * dialog's own padding would frame it with a dead margin.
   */
  bodyClassName?: string;
}

const SIZES: Record<ModalSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
};

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open dialogs, so nested/stacked ones do not each restore body scroll on close. */
let openCount = 0;

/**
 * Open dialogs, innermost last. Only the top one answers Escape and traps Tab —
 * otherwise a dialog opened over another (the committee card over a CF record)
 * closed both on one Escape, because each listens on document.
 */
const dialogStack: object[] = [];

/**
 * The one dialog primitive. Prefer <FormModal> or <HelpModal>; reach for this
 * directly only when a dialog is genuinely neither.
 *
 * Radius is the card's 16px, not a larger value: radius encodes LEVEL, not
 * elevation. A dialog is a Level 1 surface like a card, and it already announces
 * itself as above the page with a scrim and a shadow. Below `sm` it goes
 * full-bleed and square — a rounded corner on an edge-to-edge sheet reads as a
 * rendering fault.
 *
 * The surface comes from `--dialog-surface`, which citizen-theme.css repoints
 * under `<html data-role-theme="citizen">`, so Pro's white card and Citizen's
 * tinted one are the same component rather than two near-copies.
 *
 * That hook is <html>, not `.citizen-root`, and it has to be: this portals to
 * document.body, which sits OUTSIDE the div carrying `.citizen-root`, so anything
 * scoped to that class cannot reach a dialog. Same reason the dark variables live
 * on `.dark` at the document root.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  onClose,
  children,
  footer,
  size = 'md',
  description,
  titleAccessory,
  subheader,
  dismissLocked = false,
  elevation = 'raised',
  className = '',
  bodyClassName = 'min-h-0 flex-1 overflow-y-auto px-5 py-4',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  /*
    Callers pass `onClose` as an inline arrow (new identity every render), so it
    must NOT be an effect dependency — re-running the focus effect on each parent
    re-render would call panel.focus() and steal focus from whatever input the
    user is typing in. Same for dismissLocked, which flips as a form goes dirty.
  */
  const onCloseRef = useRef(onClose);
  const lockedRef = useRef(dismissLocked);
  useEffect(() => {
    onCloseRef.current = onClose;
    lockedRef.current = dismissLocked;
  }, [onClose, dismissLocked]);

  const requestClose = useCallback(() => {
    if (!lockedRef.current) onCloseRef.current();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();

    // Body scroll lock, ref-counted so closing an inner dialog does not restore
    // scrolling while an outer one is still open.
    //
    // A class, not `body.style.overflow` — index.css sets `overflow: auto
    // !important` on body to neutralise Ionic's structure.css, and an inline
    // style loses to !important. Setting it directly looked correct and did
    // nothing.
    openCount += 1;
    document.body.classList.add('dialog-open');
    const token = {};
    dialogStack.push(token);

    const onKeyDown = (event: KeyboardEvent) => {
      if (dialogStack[dialogStack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        requestClose();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap. Without it Tab walks out of the dialog and into the page
      // behind the scrim, which is invisible to a sighted user and a dead end
      // for a keyboard one.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const at = dialogStack.indexOf(token);
      if (at >= 0) dialogStack.splice(at, 1);
      openCount = Math.max(0, openCount - 1);
      if (openCount === 0) document.body.classList.remove('dialog-open');
      previouslyFocused.current?.focus?.();
    };
  }, [isOpen, requestClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
        /*
          `text-left` is deliberate. A dialog portals to <body>, so it escapes
          layout inheritance from its trigger — but not from <body> itself, and
          not from anything a future global rule puts there. The help modal
          shipped this guard after a trigger inside a centred wizard heading
          centre-aligned the whole dialog body; it belongs on every dialog, not
          just that one.
        */
        className={`flex max-h-[92vh] w-full flex-col overflow-hidden border border-dialog-border bg-dialog-surface text-left outline-none sm:max-h-[85vh] sm:rounded-2xl ${
          SIZES[size]
        } ${elevation === 'raised' ? 'shadow-xl dark:shadow-none' : 'shadow-md dark:shadow-none'} ${className}`}
      >
        {/* Sticky header: the title stays visible while a long body scrolls. */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-dialog-border px-5 py-4">
          <div className="min-w-0">
            <h2
              id={titleId}
              className="flex items-center gap-2 text-lg font-semibold text-text-primary dark:text-text-dark-primary"
            >
              {title}
              {titleAccessory}
            </h2>
            {description && (
              <p
                id={descriptionId}
                className="mt-0.5 text-sm text-text-secondary dark:text-text-dark-secondary"
              >
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="-mr-1 shrink-0 rounded-lg p-1 text-text-tertiary transition-colors hover:bg-background-tertiary hover:text-text-primary dark:text-text-dark-tertiary dark:hover:bg-background-dark-tertiary dark:hover:text-text-dark-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {subheader && (
          <div className="shrink-0 border-b border-dialog-border px-5 py-3">{subheader}</div>
        )}

        <div className={bodyClassName}>{children}</div>

        {footer && (
          <div className="flex shrink-0 items-center justify-end gap-2 border-t border-dialog-border px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
